import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getAdminClients,
  createClient,
  updateClient,
  deleteClient,
  regenerateClientCode,
  inviteClient,
  viewDocument,
} from '../../../../services/clients/admin';

export const adminClientsQueryKeys = {
  all: ['adminClients'],
  list: (params) => [...adminClientsQueryKeys.all, 'list', params],
};

export function useAdminClientsQuery(params) {
  return useQuery({
    queryKey: adminClientsQueryKeys.list(params),
    queryFn: () => getAdminClients(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCreateClient({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createClient,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.all });
      onSuccess?.(data, variables);
    },
    onError,
  });
}

export function useUpdateClient({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateClient,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.all });
      onSuccess?.(data, variables);
    },
    onError,
  });
}

export function useDeleteClient({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteClient,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.all });
      onSuccess?.(data, variables);
    },
    onError,
  });
}

export function useRegenerateClientCode({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateClientCode,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.all });
      onSuccess?.(data, variables);
    },
    onError,
  });
}

// No cache to invalidate — inviting doesn't change the client list itself,
// it just generates a code/message to hand off.
export function useInviteClient({ onSuccess, onError } = {}) {
  return useMutation({
    mutationFn: inviteClient,
    onSuccess,
    onError,
  });
}

export function useViewDocument({ onError } = {}) {
  return useMutation({
    mutationFn: viewDocument,
    onSuccess: (data) => {
      const url = data?.documentUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    onError,
  });
}
